import { mapValues, chain, size, get, map, pickBy, find, forEach, set } from "lodash"
import { createSelector } from "reselect"
import { classificationsMap } from "helpers/classification"

// These reselect selectors are used in both securities-finder-facets & watchlist-facets
// The selectors passed as arguments to 'createSelector' differ between the two modules
// Delay evaluation, use correct selectors as arguments

export const createGetClassificationsTiersSelector = (getRawClassifications, getSelectedDate) => createSelector(
  [getRawClassifications, getSelectedDate],
  (classifications, selectedDate) => classificationsMap(classifications, selectedDate),
)

export const createGetClassificationsSelector = (getRawClassificationsValues, getClassificationsTiers) => createSelector(
  [getRawClassificationsValues, getClassificationsTiers],
  (values, tiers) => mapValues(values, (c, key) =>
    getFacets(get(c, "version.values"),
      chain(tiers)
        .find(({ id }) => id === key)
        .get("tiers", [])
        .value(),
    ),
  ),
)

export const createGetCountriesMapSelector = (getModelCountries) => createSelector(
  [getModelCountries],
  (countries) => chain(countries)
    .map(({ value, label }) => ([value, label]))
    .fromPairs()
    .value(),
)

export const createGetCurrenciesMapSelector = (getModelCurrencies) => createSelector(
  [getModelCurrencies],
  (currencies) => chain(currencies)
    .map(({ value, label }) => ([value, label]))
    .fromPairs()
    .value(),
)

export const createGetCountriesSelector = (getRawCountries, getCountriesMap, getSelectedCountries) => createSelector(
  [getRawCountries, getCountriesMap, getSelectedCountries],
  (rawCountries, countries, selectedCountries) => map(rawCountries, ({ id, count }) => ({
    id,
    count,
    label: countries[id],
    selected: !!get(selectedCountries, id),
  })),
)

export const createGetClassificationFacetsSelector = (getRawClassificationsFacets, getClassifications) => createSelector(
  [getRawClassificationsFacets, getClassifications],
  (rawFacets, classifications) => {
    return mapValues(rawFacets,
      (f, cId) => map(f, ({ id, count }) => ({
        ...chain(classifications).get(cId).find(({ value }) => value === id).value(),
        id,
        count,
      })),
    )
  },
)

export const createGetSelectedClassificationsSelector = (getClassificationFacets, getRawSelectedClassifications) => createSelector(
  [getClassificationFacets, getRawSelectedClassifications],
  (allFacets, rawSelected) => {
    const selected = mapValues(rawSelected,
      (classification, cId) => pickBy(classification, (facet, facetId) => chain(allFacets)
        .get(cId)
        .find(({ value, parent }) => value === facetId || find(parent, ({ id: tierId }) => tierId === facetId))
        .value(),
      ),
    )
    return selected
  },
)

export const createGetSelectedClassificationsFilteredSelector = (getSelectedClassifications, getClassifications) => createSelector(
  [getSelectedClassifications, getClassifications],
  (selectedC, classifications) => mapValues(selectedC, (c, type) => chain(c)
    .map((facet, id) => ({
      id,
      selected: facet.selected,
      label: chain(classifications).get(type).find((item) => item.value === id).get("label")
        .value(),
    }))
    .filter(({ selected }) => selected)
    .value(),
  ),
)

export const createGetClassificationFacetTreesSelector = (
  getClassificationFacets,
  getClassificationsTiers,
  getSelectedClassifications,
) => createSelector(
  [getClassificationFacets, getClassificationsTiers, getSelectedClassifications],
  (allFacets, tiers, selectedClassifications) => mapValues(allFacets, (facets, key) => {
    const cFacetTiers = {
      ...chain(tiers)
        .find(({ id }) => id === key)
        .get("tiers")
        .map(t => ({ ...t, facets: {} }))
        .mapKeys(t => t.id)
        .value(),
    }

    // Go through the lower tier facets and build the following structure structure
    // {
    //   [tier1]: { facets: [...tier1Facets], id, level, name },
    //   [tier2]: { facets: [...tier2Facets], id, level, name },
    //   [tier3]: { facets: [...tier3Facets], id, level, name },
    //   ...
    // }
    forEach(facets, facet => {
      const tierId = get(facet, "tier.id")
      if (tierId) {
      // Deepest tier
        set(cFacetTiers, [tierId, "facets", facet.id], {
          id: facet.id,
          label: facet.label,
          count: facet.count,
          selected: get(selectedClassifications, [key, facet.id, "selected"], false),
        })
      }
      // Parent tiers
      forEach(facet.parent, parentTier => {
        const parentTierId = get(parentTier, "tier.id")
        if (parentTierId) {
          const path = `${parentTierId}.facets.${parentTier.id}`
          const existing = get(cFacetTiers, path, { count: 0 })
          set(cFacetTiers, path, {
            ...existing,
            id: parentTier.id,
            label: parentTier.name,
            count: existing.count + facet.count,
            selected: get(selectedClassifications, [key, parentTier.id, "selected"], false),
          })
        }
      })
    })
    return cFacetTiers
  }),
)

export const createGetCurrenciesSelector = (getRawCurrencies, getCurrenciesMap, getSelectedCurrencies) => createSelector(
  [getRawCurrencies, getCurrenciesMap, getSelectedCurrencies],
  (rawCurrencies, currencies, selectedCurrencies) => map(rawCurrencies, ({ id, count }) => ({
    id,
    count,
    label: currencies[id],
    selected: !!get(selectedCurrencies, id),
  })),
)

export const createGetFacetTypesSelector = (getCountries, getCurrencies, getClassificationFacetTrees, getSelectedClassificationsFiltered) => createSelector(
  [getCountries, getCurrencies, getClassificationFacetTrees, getSelectedClassificationsFiltered],
  (countries, currencies, classifications, selectedClassificationsFiltered) => [{
    type: "classification",
    facets: classifications,
    selected: selectedClassificationsFiltered,
  }, {
    type: "country",
    facets: countries,
    selected: countries.filter(d => d.selected),
  }, {
    type: "currency",
    facets: currencies,
    selected: currencies.filter(d => d.selected),
  }],
)

function getFacets(items, tiers, parent = []) {
  return chain(items)
    .map((r) => {
      if (r.values) {
        return [{
          value: r.id,
          label: r.name,
          parent,
          tier: tiers[size(parent)],
        }, getFacets(r.values, tiers, [...parent, { id: r.id, name: r.name, tier: tiers[size(parent)] }])]
      }
      return {
        value: r.id,
        label: r.name,
        parent,
        tier: tiers[size(parent)],
      }
    })
    .flattenDeep()
    .value()
}
